'use client';

import { ChangeEvent, FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { Input, Label } from '@/components/elements';

interface IInputFields {
  email: string;
  password: string;
}

interface IMobileLoginFormProps {
  switchForm: () => void;
  handleClose: () => void;
}

export const MobileLoginForm = ({ switchForm, handleClose }: IMobileLoginFormProps) => {
  const router = useRouter();
  const [errors, setErrors] = useState<Record<keyof IInputFields, boolean>>({ email: false, password: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState<IInputFields>(() => ({ email: '', password: '' }));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputs((prevValue) => ({ ...prevValue, [e.target.name]: e.target.value }));
    setErrors((prevValue) => ({ ...prevValue, [e.target.name]: false }));
    setErrorMessage(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    // Basic validation
    let hasErrors = false;
    if (!inputs.email || !inputs.email.includes('@')) {
      setErrors((prev) => ({ ...prev, email: true }));
      hasErrors = true;
    }

    if (!inputs.password || inputs.password.length < 6) {
      setErrors((prev) => ({ ...prev, password: true }));
      hasErrors = true;
    }

    if (hasErrors) {
      setIsLoading(false);
      return;
    }

    const result = await signIn("credentials", {
      email: inputs.email,
      password: inputs.password,
      redirect: false, // Don't redirect, just handle in the modal
    });

    setIsLoading(false);

    if (result?.error) {
      console.error('Login error:', result?.error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } else {
      handleClose();
      // if (userType === 'provider') router.push(paths.registerTourOperater());
      router.refresh();
    }
  };

  const isDisabled = !inputs.email.length || !inputs.email.includes('@') || inputs.password.length < 6;

  return (
    <>
      <div className="px-8 flex flex-col gap-16">
        <div className="profile-modal__header relative">
          <h2 className="text-2xl">Sign in to FaunaExplore</h2>
        </div>

        <form className="flex flex-col gap-10" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Label>Email address</Label>
                <Input.Controlled name="email" value={inputs.email} handleChange={handleChange} placeholder="Enter" />
                <p className="text-error-500 mb-3">
                  {!!errors.email ? 'Invalid email address' : ''} {errorMessage?.includes('email') ? errorMessage : ''}
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <Label>Password</Label>
                <Input.Password
                  name="password"
                  value={inputs.password}
                  handleChange={handleChange}
                  placeholder="Enter"
                />
                <p className="text-error-500 mb-3">
                  {!!errors.password ? 'Invalid password' : ''} {errorMessage?.includes('password') ? errorMessage : ''}
                </p>
              </div>
            </div>
            <span className="text-sm font-semibold cursor-pointer">Forgot your password?</span>
          </div>

          <button
            className={`btn w-full ${isDisabled || isLoading ? 'disabled' : ''}`}
            type="submit"
            disabled={isDisabled || isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
      <div className="sticky lg:relative h-20 px-8 pb-2 lg:pb-12 filter-modal__footer border-gray-800 bottom-0 text-center">
        <span>
          Not a member yet?{' '}
          <span className="font-semibold cursor-pointer" onClick={switchForm}>
            Create account
          </span>
        </span>
      </div>
    </>
  );
};
